/**
 * Pure presenters for the Exchange surface.
 *
 * Everything here maps a backend payload onto a view-model. No domain truth
 * is computed: eligibility, operational fit, ranking, provenance and
 * verification all arrive decided from the backend, and this module only
 * decides how to *say* them.
 *
 * It is deliberately dependency-free so the repository's existing Jest
 * harness can drive it with literal backend payloads (see the sibling
 * .test.js), which is where the honesty rules below are actually enforced.
 */

// ---------------------------------------------------------------------------
// Backend sentinels
// ---------------------------------------------------------------------------

/** `value` is never null for a missing fact — it is the string "UNKNOWN". */
export const UNKNOWN = 'UNKNOWN';
/** Two or more distinct stated values; the backend picks no winner. */
export const CONFLICT = 'CONFLICT';

export const isUnknownValue = (value) => value === UNKNOWN;
export const isConflictValue = (value) => value === CONFLICT;

/**
 * Read one demand-profile field envelope ({value, source, provenance, ...})
 * or one passport composed field ({value, sources} | {value, candidates}).
 *
 * Returns a uniform view-model so callers never touch `.sources` on a
 * CONFLICT shape (where the key does not exist) or print the literal words
 * UNKNOWN / CONFLICT as if they were data.
 */
export const readField = (field) => {
  if (field === null || field === undefined) {
    return { state: 'UNKNOWN', value: null, provenance: null, sources: [], candidates: [] };
  }
  // Bare scalars (package.title, location.resolutionMethod, …) are not envelopes.
  if (typeof field !== 'object') {
    return { state: 'KNOWN', value: field, provenance: null, sources: [], candidates: [] };
  }
  const raw = field.value;
  const sources = Array.isArray(field.sources)
    ? field.sources
    : field.source
      ? [field.source]
      : [];
  if (isConflictValue(raw)) {
    return {
      state: 'CONFLICT',
      value: null,
      provenance: field.provenance ?? null,
      sources: [],
      candidates: Array.isArray(field.candidates) ? field.candidates : [],
    };
  }
  if (raw === undefined || raw === null || isUnknownValue(raw)) {
    return {
      state: 'UNKNOWN',
      value: null,
      provenance: field.provenance ?? null,
      sources,
      candidates: [],
    };
  }
  return {
    state: 'KNOWN',
    value: raw,
    provenance: field.provenance ?? null,
    sources,
    candidates: [],
  };
};

/**
 * A required-or-unknown flag (credentials.insuranceRequired,
 * credentials.bonding.required). The backend never writes `false` — absence
 * is a data gap, not evidence that nothing is required — so this must never
 * collapse UNKNOWN into "not required".
 */
export const readRequirementFlag = (field) => {
  const read = readField(field);
  if (read.state === 'KNOWN' && read.value === true) {
    return { state: 'REQUIRED', sources: read.sources };
  }
  return { state: 'UNKNOWN', sources: read.sources };
};

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

/**
 * Two vocabularies land in `provenance` depending on the field family: the
 * demand engine's own labels, and (on specification/credential/security
 * rows) the requirement verification ladder copied verbatim.
 */
const PROVENANCE_LABELS = {
  // Demand engine
  OPERATOR_ENTERED: { label: 'Operator entered', tone: 'operator' },
  DERIVED_RULE: { label: 'Derived rule', tone: 'derived' },
  SCHEDULE_DERIVED: { label: 'Schedule derived', tone: 'derived' },
  PROJECT_RECORD: { label: 'Project record', tone: 'record' },
  ERS_ENGINE: { label: 'ERS engine', tone: 'derived' },
  PLANNED_BASELINE: { label: 'Planned baseline', tone: 'record' },
  APPROVED_BASELINE: { label: 'Approved baseline', tone: 'evidenced' },
  COMMERCIAL_LINE: { label: 'Commercial line', tone: 'record' },
  EXECUTION_PROJECTION: { label: 'Execution projection', tone: 'derived' },
  BACK_CALCULATED: { label: 'Back-calculated', tone: 'derived' },
  // Requirement verification ladder (copied, never upgraded)
  UNVERIFIED: { label: 'Unverified', tone: 'unknown' },
  INFERRED: { label: 'Assumption', tone: 'assumption' },
  DOCUMENT_CITED: { label: 'Document cited', tone: 'evidenced' },
  HUMAN_VERIFIED: { label: 'Human verified', tone: 'evidenced' },
  CONFLICTING: { label: 'Conflicting', tone: 'conflict' },
  SUPERSEDED: { label: 'Superseded', tone: 'unknown' },
};

export const provenanceLabel = (provenance) => {
  if (!provenance) return null;
  return PROVENANCE_LABELS[provenance] || { label: String(provenance), tone: 'unknown' };
};

// ---------------------------------------------------------------------------
// Eligibility / fit / relationship — kept separate, never fused
// ---------------------------------------------------------------------------

/**
 * Eligibility is a gate result, not a position on a scale. There is
 * deliberately no numeric rank here: the moment these three carry numbers,
 * something downstream will be tempted to average them with a fit score.
 */
export const ELIGIBILITY_DISPLAY = {
  PASS: { label: 'ELIGIBLE', tone: 'pass' },
  UNKNOWN: { label: 'ELIGIBILITY UNKNOWN', tone: 'unknown' },
  FAIL: { label: 'INELIGIBLE', tone: 'fail' },
};

export const eligibilityDisplay = (status) =>
  ELIGIBILITY_DISPLAY[status] || ELIGIBILITY_DISPLAY.UNKNOWN;

/**
 * Gate keys are engine identifiers, not English. The simple ones are single
 * tokens (`insurance`, `lead_time`); the credential ones are composite
 * (`license:MD-EC`, `certification:OSHA 30`, `small_business:WBE`).
 *
 * A PM reading "who can't do this work" should not have to parse an
 * identifier, so this names the gate family and carries the specific
 * credential separately. Nothing is invented — the qualifier is the
 * backend's own value.
 */
const GATE_FAMILY_LABELS = {
  listing_type: 'Listing type',
  active: 'Listing status',
  verification: 'Verification',
  trade: 'Trade',
  csi_division: 'CSI division',
  insurance: 'Insurance',
  bonding: 'Bonding',
  geography: 'Service area',
  lead_time: 'Lead time',
  minimum_order: 'Minimum order',
  specification: 'Specification',
  license: 'License',
  certification: 'Certification',
  small_business: 'Small business status',
};

export const gateKeyLabel = (key) => {
  const raw = String(key ?? '');
  const [family, ...rest] = raw.split(':');
  const label = GATE_FAMILY_LABELS[family];
  if (!label) return { label: raw, qualifier: null, known: false };
  const qualifier = rest.join(':') || null;
  return { label: qualifier ? `${label} — ${qualifier}` : label, qualifier, known: true };
};

/**
 * Split a pre-formatted `"{gateKey}: {detail}"` disqualification into its
 * parts. If the leading token is not a gate key this recognises, the string
 * is returned verbatim rather than being cut at an arbitrary colon —
 * mangling the backend's own sentence would be worse than a raw key.
 */
export const parseDisqualification = (line) => {
  const text = String(line ?? '');
  const split = text.indexOf(': ');
  if (split > 0) {
    const key = text.slice(0, split);
    const parsed = gateKeyLabel(key);
    if (parsed.known) {
      return { gateKey: key, gateLabel: parsed.label, detail: text.slice(split + 2) };
    }
  }
  return { gateKey: null, gateLabel: null, detail: text };
};

/**
 * Backend record-type literals. There are TWO overlapping vocabularies and
 * this must cover both, because the same presenter names identity links and
 * shortlist entries:
 *   models/exchange_identity.py LINK_RECORD_TYPES
 *     — company, vendor, subcontractor_company
 *   models/exchange_relationships.py CANDIDATE_RECORD_TYPES
 *     — vendor, subcontractor_company, network_organization, marketplace_listing
 *
 * A listing carrying neither a vendor nor a subcontractor ref is stored as
 * `marketplace_listing`, and on the shortlist that label IS the row's heading
 * whenever the listing join misses — so an uncovered token would surface as
 * the provider's name.
 */
const RECORD_TYPE_LABELS = {
  company: 'Company record',
  vendor: 'Vendor record',
  subcontractor_company: 'Subcontractor record',
  network_organization: 'Network organization',
  marketplace_listing: 'Marketplace listing',
};

export const recordTypeLabel = (recordType) =>
  RECORD_TYPE_LABELS[recordType] || String(recordType || 'Linked record');

/** Last segment of an opaque id, for humans scanning a list. Never the only identifier shown. */
export const shortId = (id) => {
  const text = String(id ?? '');
  if (text.length <= 8) return text;
  return `…${text.slice(-8)}`;
};

/**
 * Passport conflict rows are keyed by the composer's dotted path
 * (`geography.city`). Those paths are internal; the field name is what a
 * reader needs.
 */
const CONFLICT_FIELD_LABELS = {
  'identity.legalName': 'Legal name',
  'contact.email': 'Email',
  'contact.phone': 'Phone',
  'contact.website': 'Website',
  'geography.city': 'City',
  'geography.state': 'State',
  'geography.postalCode': 'Postal code',
};

export const conflictFieldLabel = (path) => {
  const known = CONFLICT_FIELD_LABELS[path];
  if (known) return known;
  const leaf = String(path ?? '').split('.').pop() || String(path ?? '');
  const spaced = leaf.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replaceAll('_', ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** Calendar day from an ISO date or timestamp. Never invents a time zone shift. */
export const formatDay = (value) => {
  if (!value) return null;
  const text = String(value);
  return text.length >= 10 ? text.slice(0, 10) : text;
};

/**
 * The view-model for one candidate.
 *
 * Rules encoded here, each of which the backend contract requires:
 *  - A FAIL candidate never shows a fit score. The backend withholds it
 *    (score null) and ships the reason; summing `factors` to backfill one
 *    would reconstruct a number it deliberately refused.
 *  - UNKNOWN eligibility DOES carry a score — "has a score" must never be
 *    rendered as "qualified".
 *  - Relationship strength can never promote a candidate: it is reported
 *    beside eligibility, never folded into it.
 *  - There is no organizationId on a candidate; a passport can only be
 *    opened through a CONFIRMED identity link.
 */
export const presentCandidate = (candidate) => {
  const eligibilityStatus = candidate?.eligibility?.status || 'UNKNOWN';
  const display = eligibilityDisplay(eligibilityStatus);
  const isFail = eligibilityStatus === 'FAIL';
  const fitScore = candidate?.operationalFit?.score;
  const relationship = candidate?.relationshipContext || {};
  const confirmedOrgIds = relationship?.identity?.confirmedOrganizationIds || [];

  return {
    listingId: candidate?.listingId,
    title: candidate?.title || 'Untitled listing',
    listingType: candidate?.listingType || null,
    eligibility: {
      status: eligibilityStatus,
      label: display.label,
      tone: display.tone,
      // Pre-formatted "{gate}: {detail}" strings from the backend.
      disqualifications: candidate?.eligibility?.disqualifications || [],
      remediation: candidate?.eligibility?.remediation || [],
      gates: candidate?.eligibility?.gateResults || [],
      unverifiedGates: (candidate?.eligibility?.gateResults || [])
        .filter((gate) => gate?.outcome === 'UNKNOWN')
        .map((gate) => gate.key),
    },
    // Null for every FAIL candidate — render the backend's explanation.
    fit: {
      score: isFail ? null : (typeof fitScore === 'number' ? fitScore : null),
      scoreWithheld: isFail,
      explanation: candidate?.operationalFit?.explanation || null,
      factors: candidate?.operationalFit?.factors || [],
    },
    relationship: {
      known: Boolean(relationship?.knownProvider),
      quotes: relationship?.quotes?.count || 0,
      selectedQuotes: relationship?.quotes?.selectedCount || 0,
      interests: relationship?.interests?.count || 0,
      bookings: relationship?.bookings?.count || 0,
      memberships: relationship?.memberships?.count || 0,
      /**
       * This flag means the per-record `sources` LISTING was cut at its
       * bound — the counts beside it are exact totals over every row read.
       * Reporting it as "these counts are a floor" would invert the
       * backend's own statement.
       */
      sourcesTruncated: Boolean(relationship?.sourcesTruncated),
      // Backend-authored disclosure; display verbatim.
      note: relationship?.note || null,
    },
    /**
     * A passport is opened through a CONFIRMED identity link. A listing can
     * carry both a vendor and a subcontractor ref, each confirmed to a
     * DIFFERENT organization, so more than one id here is a real state —
     * picking the first would name one identity and silently hide the other.
     */
    passportOrganizationId: confirmedOrgIds.length === 1 ? confirmedOrgIds[0] : null,
    confirmedIdentityCount: confirmedOrgIds.length,
    confirmedOrganizationIds: confirmedOrgIds,
    whyMatched: candidate?.whyMatched || null,
    whyExcluded: candidate?.whyExcluded || null,
    missingData: candidate?.missingData || [],
    confidence: candidate?.confidence || null,
    dataAgeDays: typeof candidate?.dataAgeDays === 'number' ? candidate.dataAgeDays : null,
  };
};

/**
 * Group candidates for display.
 *
 * The backend orders engine-qualified candidates by score (interleaving PASS
 * and UNKNOWN) and then appends FAIL alphabetically. Grouping by the derived
 * status is presentation, and it keeps an ineligible provider from sitting
 * among eligible ones purely because it sorted well.
 *
 * Within each group the backend's relative order is preserved — this never
 * re-ranks.
 */
export const groupCandidates = (candidates = []) => {
  const groups = { PASS: [], UNKNOWN: [], FAIL: [] };
  candidates.map(presentCandidate).forEach((candidate) => {
    (groups[candidate.eligibility.status] || groups.UNKNOWN).push(candidate);
  });
  return groups;
};

/**
 * What an empty candidate list means.
 *
 * An empty result is a catalog gap — no listings existed to match against —
 * NOT a finding that nobody qualifies. The backend says so explicitly in
 * `missingInformation`; this keeps the UI from inventing the harsher claim.
 */
export const emptyCandidateMeaning = (matches) => {
  const total = matches?.candidateCount ?? (matches?.candidates || []).length;
  if (total > 0) return null;
  return {
    title: 'No providers to compare yet',
    detail:
      'No marketplace listings were available to match against this package. ' +
      'That is a gap in the provider catalog — not a finding that no provider qualifies.',
  };
};

// ---------------------------------------------------------------------------
// Project assembly
// ---------------------------------------------------------------------------

/**
 * The sourcing state of one package, derived strictly from records that
 * already exist: the package's own status, its durable quote count, and
 * (when it has been loaded) the backend's candidate counts.
 *
 * There is deliberately no coverage percentage: nothing in the backend
 * expresses "this project is 62% sourced", so neither does this.
 *
 * `matchCounts` is the backend `counts` object ({pass, fail, unknown},
 * lowercase keys) or null when matches have not been loaded for this row.
 *
 * Options:
 *   `sourcingProbed`  this package's candidates were actually checked. A row
 *                     that was never checked is SOURCING UNKNOWN, never
 *                     UNSOURCED — "we did not look" is not "nothing is there".
 *   `matchesUnavailable`  matching refused or failed for this row.
 *
 * Package statuses are the backend's own (models/procurement.py
 * BID_STATUSES): upcoming, pending, submitted, awarded, missed, cancelled.
 */
export const ASSEMBLY_STATES = {
  MISSED: { label: 'BID DATE MISSED', tone: 'attention', order: 0 },
  // A prepared handoff is waiting on a named human, so it outranks the
  // commercial states that are merely progressing on their own.
  RFQ_PREPARED: { label: 'RFQ PREPARED', tone: 'attention', order: 0.5 },
  EVALUATED: { label: 'EVALUATED', tone: 'active', order: 0.8 },
  QUOTED: { label: 'QUOTED', tone: 'active', order: 0.9 },
  QUOTES_RECEIVED: { label: 'QUOTES RECEIVED', tone: 'active', order: 1 },
  RELEASED_FOR_RFQ: { label: 'RELEASED — NO RESPONSE', tone: 'active', order: 1.5 },
  RFQ_OPEN: { label: 'RFQ OPEN', tone: 'active', order: 2 },
  NEEDS_SHORTLIST: { label: 'NEEDS SHORTLIST', tone: 'attention', order: 3 },
  MATCHES_FOUND: { label: 'MATCHES', tone: 'active', order: 4 },
  UNSOURCED: { label: 'UNSOURCED', tone: 'attention', order: 5 },
  ASSIGNED: { label: 'ASSIGNED', tone: 'good', order: 6 },
  SOURCING_UNKNOWN: { label: 'SOURCING UNKNOWN', tone: 'unknown', order: 7 },
  CANCELLED: { label: 'CANCELLED', tone: 'unknown', order: 8 },
};

/**
 * The commercial half of a package's state, composed ONLY from the backend's
 * own facets on the RFQ releases surface.
 *
 * Nothing is derived here beyond choosing which backend fact to say first:
 * `downstreamFacets` is the backend's own provider-specific verdict, and
 * `stateFacets` is its own count of stored release states. React never asks
 * "does a quote exist on this package" — that question is exactly the
 * ambiguity the release table was built to remove.
 *
 * Strongest true fact first, matching the backend's own precedence.
 */
const commercialStateFromReleases = (releaseFacts) => {
  if (!releaseFacts) return null;
  const downstream = releaseFacts.downstreamFacets || {};
  const states = releaseFacts.stateFacets || {};

  /**
   * A PREPARED handoff is waiting on a named human, so it is reported FIRST.
   * Letting a downstream fact outrank it would erase the pending decision
   * from the list while the tab beside it still counts one.
   */
  if (states.PREPARED) {
    return {
      state: 'RFQ_PREPARED',
      detail: `${states.PREPARED} provider${
        states.PREPARED === 1 ? '' : 's'
      } prepared and awaiting release authorization.`,
    };
  }

  /**
   * `downstreamFacets` is tallied over EVERY release on the package —
   * prepared and revoked rows included, not only released ones. So it can say
   * that a quote came back, but it cannot say from whom, and it must never be
   * paraphrased as "a released provider": that would assert an authorization
   * the count does not report. The per-provider panel, which does know each
   * release's state, is where "released" is said.
   */
  if (downstream.AWARDED) {
    return { state: 'ASSIGNED', detail: 'A provider on this package has been awarded it.' };
  }
  if (downstream.EVALUATED) {
    return { state: 'EVALUATED', detail: 'A quote on this package has been evaluated.' };
  }
  if (downstream.QUOTED || downstream.RESPONDED) {
    const responded = (downstream.QUOTED || 0) + (downstream.RESPONDED || 0);
    return {
      state: 'QUOTED',
      detail: `${responded} provider${responded === 1 ? ' has' : 's have'} responded.`,
    };
  }
  if (states.RELEASED) {
    return {
      state: 'RELEASED_FOR_RFQ',
      detail: `${states.RELEASED} provider${
        states.RELEASED === 1 ? '' : 's'
      } released for RFQ; nothing has come back yet.`,
    };
  }
  return null;
};

export const presentAssemblyRow = (pkg, matchCounts = null, options = {}) => {
  const status = String(pkg?.status || '').toLowerCase();
  const quoteCount = pkg?.currentQuoteCount;
  const quotesKnown = typeof quoteCount === 'number';

  let state;
  let detail = null;

  if (status === 'awarded') {
    state = 'ASSIGNED';
    detail = 'Awarded — provider linked into procurement truth.';
  } else if (status === 'cancelled') {
    state = 'CANCELLED';
    detail = 'Cancelled — no longer part of this project.';
  } else if (status === 'missed') {
    state = 'MISSED';
    detail = 'Marked missed — the bid date passed without a submission.';
  } else if (commercialStateFromReleases(options.releaseFacts)) {
    // Backend release facts are more specific than a package-wide quote
    // count: they name WHICH provider answered.
    const commercial = commercialStateFromReleases(options.releaseFacts);
    state = commercial.state;
    detail = commercial.detail;
  } else if (quotesKnown && quoteCount > 0) {
    state = 'QUOTES_RECEIVED';
    detail = `${quoteCount} current quote${quoteCount === 1 ? '' : 's'} recorded.`;
  } else if (status === 'pending' || status === 'submitted') {
    state = 'RFQ_OPEN';
    detail = quotesKnown
      ? 'RFQ out; no quotes recorded yet.'
      : 'RFQ out. Quote counts need the RFQ API.';
  } else if (matchCounts) {
    const eligible = matchCounts.pass || 0;
    const unknown = matchCounts.unknown || 0;
    if (eligible + unknown > 0) {
      state = eligible > 0 ? 'MATCHES_FOUND' : 'NEEDS_SHORTLIST';
      detail =
        `${eligible} eligible` +
        (unknown > 0 ? `, ${unknown} needing verification` : '') +
        `${matchCounts.fail ? `, ${matchCounts.fail} ineligible` : ''}.`;
    } else {
      state = 'UNSOURCED';
      detail = matchCounts.fail
        ? `${matchCounts.fail} candidate${matchCounts.fail === 1 ? '' : 's'} checked, none eligible.`
        : 'No candidates available to compare.';
    }
  } else if (options.matchesUnavailable) {
    state = 'SOURCING_UNKNOWN';
    detail = 'Provider matching is unavailable, so sourcing state is unknown.';
  } else if (options.sourcingProbed) {
    // Probed, and the backend returned no counts object at all.
    state = 'SOURCING_UNKNOWN';
    detail = 'Provider matching returned no candidate counts for this package.';
  } else {
    // Never checked. Saying UNSOURCED here would report an absence that was
    // never looked for.
    state = 'SOURCING_UNKNOWN';
    detail = 'Not checked yet — open this package to see who can fulfil it.';
  }

  const spec = ASSEMBLY_STATES[state];
  return {
    packageId: pkg?.bidPackageId || pkg?.id,
    title: pkg?.title || 'Untitled package',
    trade: pkg?.trade || null,
    dueDate: formatDay(pkg?.dueDate || pkg?.due_date),
    state,
    label: spec.label,
    tone: spec.tone,
    order: spec.order,
    detail,
    quoteCount: quotesKnown ? quoteCount : null,
    matchCounts,
  };
};

/** Assembly rows ordered by what needs attention first, then by title. */
export const sortAssemblyRows = (rows = []) =>
  [...rows].sort(
    (a, b) => a.order - b.order || String(a.title).localeCompare(String(b.title))
  );

/**
 * The one-line answer to "what does this project still need?" — counts of
 * real states, never a synthesized percentage.
 *
 * SOURCING_UNKNOWN is counted on its own and is NOT folded into
 * "needs attention": a package nobody has checked yet is not evidence of a
 * problem, and inflating the attention count with unchecked rows would make
 * the number mean nothing.
 */
export const summarizeAssembly = (rows = []) => {
  const summary = {
    total: rows.length,
    needsAttention: 0,
    assigned: 0,
    inCommercial: 0,
    readyToSource: 0,
    unsourced: 0,
    unknown: 0,
    closed: 0,
  };
  rows.forEach((row) => {
    if (row.state === 'ASSIGNED') summary.assigned += 1;
    else if (
      ['QUOTES_RECEIVED', 'RFQ_OPEN', 'QUOTED', 'EVALUATED', 'RELEASED_FOR_RFQ'].includes(
        row.state
      )
    )
      summary.inCommercial += 1;
    // A prepared handoff is waiting on a person, so it belongs with the work
    // that needs attention rather than with what is already moving.
    else if (row.state === 'RFQ_PREPARED') summary.readyToSource += 1;
    // A package with eligible candidates waiting is not "unsourced" and not
    // "unknown" — it has somewhere to go, and it needs its own bucket or it
    // disappears from every tile while still counting toward the total.
    else if (['MATCHES_FOUND', 'NEEDS_SHORTLIST'].includes(row.state)) summary.readyToSource += 1;
    else if (row.state === 'UNSOURCED') summary.unsourced += 1;
    else if (row.state === 'SOURCING_UNKNOWN') summary.unknown += 1;
    else if (row.state === 'CANCELLED') summary.closed += 1;
    if (['MISSED', 'NEEDS_SHORTLIST', 'UNSOURCED', 'RFQ_PREPARED'].includes(row.state)) {
      summary.needsAttention += 1;
    }
  });
  return summary;
};

// ---------------------------------------------------------------------------
// Shortlist — automation proposes, humans decide (backend exchange-shortlist-1)
// ---------------------------------------------------------------------------

/**
 * Membership states. PROPOSED is the automation birth state and is NOT a
 * decision a human can send — it is what "nobody has judged this yet" looks
 * like, which is why it reads as AWAITING DECISION rather than as a status.
 */
export const SHORTLIST_STATE_DISPLAY = {
  PREFERRED: { label: 'PREFERRED', tone: 'good', order: 0 },
  SHORTLISTED: { label: 'SHORTLISTED', tone: 'good', order: 1 },
  PROPOSED: { label: 'AWAITING DECISION', tone: 'attention', order: 2 },
  EXCLUDED: { label: 'EXCLUDED', tone: 'bad', order: 3 },
  REMOVED: { label: 'REMOVED', tone: 'unknown', order: 4 },
};

export const shortlistStateDisplay = (state) =>
  SHORTLIST_STATE_DISPLAY[state] || { label: String(state || 'Unknown'), tone: 'unknown', order: 5 };

/**
 * backend/services/exchange_shortlist.py ALLOWED_TRANSITIONS, verbatim.
 *
 * Note what is absent: no self-transition. Re-affirming SHORTLISTED is a 409,
 * not a no-op, so the current state must never be offered as an action.
 * EXCLUDED and REMOVED have exactly one way back, and it is not PREFERRED.
 */
export const SHORTLIST_TRANSITIONS = {
  PROPOSED: ['SHORTLISTED', 'EXCLUDED'],
  SHORTLISTED: ['PREFERRED', 'EXCLUDED', 'REMOVED'],
  PREFERRED: ['SHORTLISTED', 'EXCLUDED', 'REMOVED'],
  EXCLUDED: ['SHORTLISTED'],
  REMOVED: ['SHORTLISTED'],
};

export const shortlistDecisionsFrom = (state) => SHORTLIST_TRANSITIONS[state] || [];

/** The only two states RFQ preparation accepts (contract exchange-rfq-1 §3.1). */
export const PREPARABLE_SHORTLIST_STATES = ['SHORTLISTED', 'PREFERRED'];

/** What each decision means to the person about to make it. */
export const SHORTLIST_DECISION_COPY = {
  SHORTLISTED: { verb: 'Shortlist', past: 'shortlisted' },
  PREFERRED: { verb: 'Mark preferred', past: 'marked preferred' },
  EXCLUDED: { verb: 'Exclude', past: 'excluded' },
  REMOVED: { verb: 'Remove', past: 'removed' },
};

/**
 * The downstream lifecycle the backend derives at READ time from bid_quotes,
 * award_proposals and the package's award linkage. It is deliberately a
 * different vocabulary from membership: an entry is never *stored* as QUOTED.
 */
export const DERIVED_LIFECYCLE_DISPLAY = {
  QUOTED: 'Quoted',
  EVALUATED: 'Evaluated',
  RECOMMENDED: 'Recommended',
  AUTHORIZED: 'Authorized',
  ASSIGNED: 'Assigned',
};

/**
 * One shortlist entry as a view-model.
 *
 * The entry carries NO provider name — only recordType/recordId and the
 * listing id its match evidence recorded. The only reliable join back to a
 * candidate is `matchBasis[].listingId`, so `candidatesByListing` is the
 * current /matches result keyed by listingId. When the join misses, the
 * provider is named as unresolved rather than guessed at.
 *
 * `matchBasis` is frozen at propose time and is never refreshed by the
 * backend, so everything derived from it is labelled as of the proposal, not
 * as current truth.
 */
export const presentShortlistEntry = (entry, candidatesByListing = {}) => {
  const basis = (entry?.matchBasis || []).find((row) => row?.kind === 'exchange_match') || null;
  const listingId = basis?.listingId || null;
  const candidate = listingId ? candidatesByListing[listingId] || null : null;
  const display = shortlistStateDisplay(entry?.state);
  const derivedStates = entry?.derived?.states || [];
  const isOrganizationEntry = entry?.recordType === 'network_organization';

  return {
    entryId: entry?.id,
    state: entry?.state,
    label: display.label,
    tone: display.tone,
    order: display.order,

    listingId,
    // Present only when the listing is still in the current match set.
    title: candidate?.title || null,
    // An entry whose listing no longer matches is a real situation, not an error.
    titleResolved: Boolean(candidate),
    recordType: entry?.recordType || null,
    recordLabel: recordTypeLabel(entry?.recordType),
    recordId: entry?.recordId || null,
    networkOrganizationId: entry?.networkOrganizationId || null,

    // Evidence as recorded WHEN PROPOSED. Never presented as current.
    proposedAt: formatDay(entry?.createdAt),
    proposedEligibility: basis?.eligibilityStatus || null,
    proposedScore: typeof basis?.score === 'number' ? basis.score : null,
    proposedConfidence: basis?.confidence || null,
    proposedWhyMatched: basis?.whyMatched || null,

    decidedAt: formatDay(entry?.decidedAt),
    decisionNote: entry?.decisionNote || null,
    decided: Boolean(entry?.decidedAt),

    lifecycle: derivedStates.map((row) => ({
      state: row?.state,
      label: DERIVED_LIFECYCLE_DISPLAY[row?.state] || String(row?.state || ''),
      // Evidence arrays are capped at 50 — a length is a floor, not a total.
      evidenceCount: (row?.evidence || []).length,
    })),
    /**
     * An organization-typed entry joins no records directly, so an empty
     * lifecycle means "cannot be joined", not "nothing has happened". Saying
     * the second would be inventing an absence.
     */
    lifecycleJoinable: !isOrganizationEntry,

    decisions: shortlistDecisionsFrom(entry?.state),

    /**
     * Eligible for RFQ preparation (contract exchange-rfq-1 §3.1): only
     * SHORTLISTED and PREFERRED, and only when a human actually decided it.
     *
     * PROPOSED is automation's birth state — it must never masquerade as a
     * human shortlist. EXCLUDED and REMOVED are a human deciding AGAINST.
     * The backend evaluates this against the entry's current state at prepare
     * time and refuses anything else, so offering the control on an
     * ineligible row would promise something it will not do.
     */
    preparable: PREPARABLE_SHORTLIST_STATES.includes(entry?.state) && Boolean(entry?.decidedAt),
  };
};

/** Membership first (preferred, then shortlisted), then by title. */
export const sortShortlistEntries = (rows = []) =>
  [...rows].sort(
    (a, b) =>
      a.order - b.order ||
      String(a.title || a.recordId || '').localeCompare(String(b.title || b.recordId || ''))
  );

/**
 * What the shortlist says as a whole.
 *
 * `count` from the backend is the length of an ALREADY-TRUNCATED array and
 * there is no totalCount, so this never claims to be a total when `truncated`
 * is set. `stateFacets` is likewise computed over the truncated set.
 */
export const summarizeShortlist = (payload) => {
  const facets = payload?.stateFacets || {};
  const awaiting = facets.PROPOSED || 0;
  const active = (facets.SHORTLISTED || 0) + (facets.PREFERRED || 0);
  return {
    count: payload?.count || 0,
    awaiting,
    active,
    excluded: (facets.EXCLUDED || 0) + (facets.REMOVED || 0),
    truncated: Boolean(payload?.truncated),
    // Every count above is a floor when the read was bounded.
    countsAreFloors: Boolean(payload?.truncated),
    notes: payload?.notes || [],
  };
};

/**
 * What a propose run actually did. The backend's counts do not reconcile by
 * design — `skippedFailCount` is computed over the FULL candidate list while
 * `createdEntries` is capped — so this reports them as separate statements
 * rather than arithmetic.
 */
export const summarizeShortlistProposal = (result) => {
  const created = (result?.createdEntries || []).length;
  const lines = [];
  if (result?.alreadyProposedCount) {
    lines.push(
      `${result.alreadyProposedCount} candidate${result.alreadyProposedCount === 1 ? ' was' : 's were'} already on this shortlist — including any you previously excluded, which a re-run never revives.`
    );
  }
  if (result?.skippedFailCount) {
    lines.push(
      `${result.skippedFailCount} candidate${result.skippedFailCount === 1 ? '' : 's'} failed a mandatory requirement and cannot be shortlisted.`
    );
  }
  if (result?.skippedMissingListingCount) {
    lines.push(
      `${result.skippedMissingListingCount} listing${result.skippedMissingListingCount === 1 ? ' disappeared' : 's disappeared'} between matching and recording, so ${result.skippedMissingListingCount === 1 ? 'it was' : 'they were'} not proposed.`
    );
  }
  if (result?.proposalTruncated) {
    lines.push(
      'More candidates qualified than this run could record. The highest-scoring ones were proposed; the rest were not.'
    );
  }
  if (result?.matchTruncated) {
    lines.push(
      'Matching itself read a bounded set of listings, so providers beyond that bound were never considered.'
    );
  }
  return { created, lines };
};

// ---------------------------------------------------------------------------
// Relationship context (backend exchange-relationship-1)
// ---------------------------------------------------------------------------

/**
 * The seven human-asserted event types. Every one is a statement a person
 * made; the backend refuses derived events (INVITED, BID, AWARDED…) because
 * those live in the records that own them.
 */
export const RELATIONSHIP_EVENT_TYPES = [
  { value: 'WORKED_WITH', label: 'Worked with them' },
  { value: 'MOBILIZED', label: 'They mobilized' },
  { value: 'COMPLETED', label: 'They completed the work' },
  { value: 'PERFORMANCE_RECORDED', label: 'Performance noted' },
  { value: 'WOULD_USE_AGAIN', label: 'Would use again' },
  { value: 'PREFERRED', label: 'Preferred provider' },
  { value: 'DO_NOT_RECOMMEND', label: 'Would not recommend' },
];

export const relationshipEventLabel = (eventType) =>
  RELATIONSHIP_EVENT_TYPES.find((row) => row.value === eventType)?.label ||
  String(eventType || '').replaceAll('_', ' ');

/**
 * Response rate. The backend ships a FRACTION in [0,1] or the literal string
 * "UNKNOWN", plus the components of its own denominator.
 *
 * Two things about that payload are easy to get wrong, and both are wrong in
 * a way that misreports a provider:
 *
 *  1. `invitesSent` IS the denominator behind `value` — revoked and expired
 *     invitations have ALREADY been removed from it, and `invitesExcluded`
 *     counts those removals separately. Subtracting them again would print a
 *     denominator the backend never computed, and can go negative.
 *  2. In the zero-denominator branch the backend sets `invitesSent: 0` and
 *     parks the real total in `invitesExcluded`. So "no answerable invites"
 *     is not the same claim as "no invitations were ever sent" — a provider
 *     whose every invitation was revoked has the first and not the second.
 *
 * Nothing here is recomputed: `value` is the backend's number, and this only
 * chooses how to say it. UNKNOWN is never rendered as 0% — never having held
 * a live invitation is not a failure to respond.
 */
export const presentResponseRate = (responseRate) => {
  const value = responseRate?.value;
  const invitesSent = responseRate?.invitesSent ?? null;
  const invitesExcluded = responseRate?.invitesExcluded ?? null;
  const responses = responseRate?.responses ?? null;

  if (typeof value !== 'number') {
    const excluded = invitesExcluded || 0;
    return {
      known: false,
      invitesSent,
      invitesExcluded,
      responses,
      detail:
        excluded > 0
          ? `${excluded} invitation${excluded === 1 ? ' was' : 's were'} sent and then revoked or allowed to expire, so this provider never held a live invitation to answer. That is not a failure to respond.`
          : 'No invitations to this provider are recorded, so there is no response rate to report.',
    };
  }
  return {
    known: true,
    percent: Math.round(value * 100),
    invitesSent,
    invitesExcluded,
    responses,
    // invitesSent is already net of the excluded ones.
    detail: `${responses ?? 0} of ${invitesSent ?? 0} answerable invitation${
      invitesSent === 1 ? '' : 's'
    } answered${
      invitesExcluded ? ` (${invitesExcluded} more were revoked or expired and are not counted)` : ''
    }.`,
  };
};

/**
 * The would-use-again stance.
 *
 * The backend emits TWO different shapes: with no statements it carries
 * `value: "UNKNOWN"`, and with statements it carries no `value` key at all.
 * That is deliberate — a zero tally must not read as a zero score — so this
 * branches on the statement count, never on `value`.
 *
 * There is no ratio here on purpose. Dividing these tallies would manufacture
 * exactly the score the backend refuses to compute.
 */
export const presentStance = (wouldUseAgain) => {
  const statements = wouldUseAgain?.statements || 0;
  if (statements === 0) {
    return {
      known: false,
      preferred: wouldUseAgain?.preferred || 0,
      detail: 'Nobody has recorded a view on using this provider again.',
    };
  }
  return {
    known: true,
    wouldUseAgain: wouldUseAgain?.wouldUseAgain || 0,
    doNotRecommend: wouldUseAgain?.doNotRecommend || 0,
    preferred: wouldUseAgain?.preferred || 0,
    statements,
  };
};

/**
 * A recorded statement. `occurredOn` is the string "UNKNOWN" on this read
 * model (it is null on the write echo) — an undated statement, never today.
 */
export const presentRelationshipEvent = (event) => ({
  id: event?.id,
  eventType: event?.eventType,
  label: relationshipEventLabel(event?.eventType),
  occurredOn: event?.occurredOn && event.occurredOn !== UNKNOWN ? formatDay(event.occurredOn) : null,
  dateKnown: Boolean(event?.occurredOn) && event.occurredOn !== UNKNOWN,
  note: event?.note || null,
  projectId: event?.projectId || null,
  bidPackageId: event?.bidPackageId || null,
  recordLabel: recordTypeLabel(event?.recordType),
  payload: event?.payload || {},
  evidenceCount: (event?.evidence || []).length,
});

/** Whether this organization has any CONFIRMED identity behind its context. */
export const hasConfirmedIdentity = (context) =>
  ((context?.identity?.confirmedVendorIds || []).length +
    (context?.identity?.confirmedSubcontractorCompanyIds || []).length) >
  0;

// ---------------------------------------------------------------------------
// Provider passport
// ---------------------------------------------------------------------------

/**
 * Listing verification vocabulary. "VERIFIED" is not a backend value — the
 * verified tier is split, and only these two count as verified anywhere in
 * the system (marketplace_matching._VERIFIED_STATUSES).
 */
const VERIFIED_STATUSES = ['DOCUMENT_VERIFIED', 'PLATFORM_VERIFIED'];

export const VERIFICATION_DISPLAY = {
  DOCUMENT_VERIFIED: { label: 'Document verified', tone: 'verified' },
  PLATFORM_VERIFIED: { label: 'Platform verified', tone: 'verified' },
  SELF_REPORTED: { label: 'Self reported', tone: 'self' },
  UNVERIFIED: { label: 'Unverified', tone: 'unknown' },
  EXPIRED: { label: 'Expired', tone: 'bad' },
  MISSING: { label: 'Missing', tone: 'unknown' },
  DISPUTED: { label: 'Disputed', tone: 'bad' },
  UNKNOWN: { label: 'Unknown', tone: 'unknown' },
};

export const verificationDisplay = (status) =>
  VERIFICATION_DISPLAY[status] || { label: String(status || 'Unknown'), tone: 'unknown' };

/**
 * Marketplace listing types. The passport carries no listing title, so this
 * enum IS the only label a listing gets — which makes rendering the raw
 * token the difference between "Subcontractor service" and
 * "SUBCONTRACTOR_SERVICE" as a card heading.
 */
const LISTING_TYPE_LABELS = {
  SUBCONTRACTOR_SERVICE: 'Subcontractor service',
  SUPPLIER: 'Supplier',
  MATERIAL: 'Material',
  EQUIPMENT_RENTAL: 'Equipment rental',
  PROFESSIONAL_SERVICE: 'Professional service',
  LABOR_CREW: 'Labor crew',
  WORKER: 'Worker',
  LOGISTICS_SERVICE: 'Logistics service',
};

export const listingTypeLabel = (listingType) => {
  if (!listingType) return 'Listing';
  const known = LISTING_TYPE_LABELS[listingType];
  if (known) return known;
  const spaced = String(listingType).replaceAll('_', ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const isVerifiedStatus = (status) => VERIFIED_STATUSES.includes(status);

/**
 * Identity link states. The passport's `proposed` bucket is every
 * non-CONFIRMED link, so DISPUTED and UNRESOLVED live there too — labelling
 * the whole bucket "Proposed" would misstate a standing disagreement.
 */
export const LINK_STATE_DISPLAY = {
  CONFIRMED: { label: 'Confirmed', tone: 'verified' },
  PROPOSED_MATCH: { label: 'Proposed match', tone: 'proposed' },
  DISPUTED: { label: 'Disputed', tone: 'bad' },
  UNRESOLVED: { label: 'Unresolved', tone: 'unknown' },
};

export const linkStateDisplay = (state) =>
  LINK_STATE_DISPLAY[state] || { label: String(state || 'Unknown'), tone: 'unknown' };

/**
 * Age in whole days from a raw ISO timestamp. The passport reports stored
 * timestamps only and computes no freshness grade; a null timestamp is
 * unknown age, never "0 days old".
 */
export const ageInDays = (isoTimestamp, now = new Date()) => {
  if (!isoTimestamp) return null;
  const then = new Date(isoTimestamp);
  if (Number.isNaN(then.getTime())) return null;
  const diff = Math.floor((now.getTime() - then.getTime()) / 86400000);
  return diff < 0 ? 0 : diff;
};

/**
 * Summarize passport freshness across record and listing timestamps.
 * Returns null when nothing carries a usable timestamp — the UI then says
 * the freshness is unknown rather than implying the data is current.
 */
export const summarizeFreshness = (freshness, now = new Date()) => {
  const ages = [
    ...(freshness?.records || []).map((row) => ageInDays(row?.updatedAt, now)),
    ...(freshness?.listings || []).map((row) => ageInDays(row?.dataAsOf, now)),
  ].filter((age) => age !== null);
  if (ages.length === 0) return null;
  return {
    newestDays: Math.min(...ages),
    oldestDays: Math.max(...ages),
    counted: ages.length,
  };
};

// ---------------------------------------------------------------------------
// RFQ handoff (backend exchange-rfq-1) — the commercial bridge
// ---------------------------------------------------------------------------

/**
 * Release state, in the words the contract requires.
 *
 * The whole point of this vocabulary is that PREPARED and RELEASED describe
 * INTERNAL records, and neither means anything left the building. "Sent",
 * "invited", "notified" and "contacted" are not available words here, because
 * the backend has no external send path at all.
 *
 * Whether a provider actually came back lives in a separate derived block and
 * never on `state`.
 */
export const RELEASE_STATE_DISPLAY = {
  PREPARED: {
    label: 'PREPARED',
    detail: 'Awaiting release authorization',
    tone: 'attention',
    order: 0,
  },
  RELEASED: {
    label: 'RELEASED FOR RFQ',
    detail: 'Cleared to quote',
    tone: 'good',
    order: 1,
  },
  REVOKED: {
    label: 'REVOKED',
    detail: 'Withdrawn before release',
    tone: 'unknown',
    order: 2,
  },
};

export const releaseStateDisplay = (state) =>
  RELEASE_STATE_DISPLAY[state] || {
    label: String(state || 'Unknown'),
    detail: null,
    tone: 'unknown',
    order: 3,
  };

/**
 * The derived, PROVIDER-SPECIFIC downstream state. The backend joins on this
 * release's own bidder key, so another provider quoting the same package can
 * never move this one.
 *
 * NO_RESPONSE means "released, and nothing has come back from THIS provider".
 * It emphatically does not mean "not invited" — the release row is the proof
 * that preparation happened, which is the reason the release table exists.
 */
export const DOWNSTREAM_STATE_DISPLAY = {
  AWARDED: { label: 'AWARDED', tone: 'good', order: 0 },
  EVALUATED: { label: 'EVALUATED', tone: 'active', order: 1 },
  QUOTED: { label: 'QUOTED', tone: 'active', order: 2 },
  RESPONDED: { label: 'RESPONDED', tone: 'active', order: 3 },
  NO_RESPONSE: { label: 'NO RESPONSE YET', tone: 'unknown', order: 4 },
};

export const downstreamStateDisplay = (state) =>
  DOWNSTREAM_STATE_DISPLAY[state] || {
    label: String(state || 'Unknown'),
    tone: 'unknown',
    order: 5,
  };

/**
 * What the four structural booleans actually say, in operator language.
 *
 * They are on every response of all three endpoints and are repeated on every
 * individual release. The contract is explicit that they must be rendered
 * rather than hidden behind a tooltip, and that a UI summarising them as
 * "RFQ sent" is wrong.
 *
 * This reads the BACKEND's values every time. If the backend later gains a
 * real send path and flips them, this renders the new truth without a code
 * change — nothing here hardcodes a future assumption.
 */
export const presentSendTruth = (payload) => {
  const sent = payload?.rfqSent === true;
  const notified = payload?.providerNotified === true;
  const externalEnabled = payload?.externalSendEnabled === true;
  return {
    sent,
    notified,
    externalEnabled,
    // Backend-authored operator copy; rendered verbatim, never summarised.
    notes: payload?.notes || [],
    /**
     * These booleans say ONE thing: whether anything left the building. They
     * carry no information about whether a release exists or whether a human
     * authorized one, so the copy must not borrow their certainty to assert
     * either. Saying "internal release authorization recorded" here would
     * claim a human decision on a package where nobody has prepared anything.
     */
    headline: sent
      ? 'An RFQ has been transmitted externally.'
      : 'Nothing has been sent. No RFQ has been transmitted externally.',
    providerAware: notified
      ? 'Providers on this package have been notified.'
      : 'No provider has been notified.',
  };
};

/**
 * One release as a view model.
 *
 * Three separate truths are kept apart deliberately:
 *   state       what a human has authorized internally
 *   shortlist   whether the decision it rested on still holds
 *   downstream  whether THIS provider has actually responded
 *
 * Collapsing any two of them would be the failure this surface exists to
 * prevent.
 */
export const presentRelease = (release, candidatesByOrganization = {}) => {
  const state = releaseStateDisplay(release?.state);
  const downstreamState = release?.downstream?.state || null;
  const identity = release?.scopeSnapshot?.identity || {};
  const basis = release?.shortlistBasis || {};
  /**
   * A release carries NO provider name and NO listing id — `shortlistBasis`
   * has eight keys and none is a listing, and `scopeSnapshot.identity` is a
   * subset that drops `listingId` too. The only join back to a named
   * candidate is the network organization, which both sides do carry.
   *
   * When that join misses, the record family and the bidder key identify the
   * release and nothing is guessed.
   */
  const organizationIds = [
    basis?.networkOrganizationId,
    ...(identity?.networkOrganizationIds || []),
  ].filter(Boolean);
  const candidate =
    organizationIds.map((id) => candidatesByOrganization[id]).find(Boolean) || null;

  return {
    releaseId: release?.id,
    state: release?.state,
    label: state.label,
    stateDetail: state.detail,
    tone: state.tone,
    order: state.order,

    // Commercial identity, resolved once at preparation and never moved.
    bidderKey: release?.bidderKey || null,
    recordType: release?.recordType || null,
    recordLabel: recordTypeLabel(release?.recordType),
    recordId: release?.recordId || null,
    /**
     * A release carries no provider name. The title comes from the current
     * match set when the listing is still there; otherwise the record family
     * and bidder key identify it, and nothing is guessed.
     */
    title: candidate?.title || null,
    titleResolved: Boolean(candidate),
    identityConfirmed: identity?.identityConfirmed === true,
    tiebreak: identity?.tiebreak || null,

    shortlistEntryId: release?.shortlistEntryId || null,
    shortlistBasisState: basis?.state || null,
    preparedAt: formatDay(release?.preparedAt),
    preparedBy: release?.preparedBy || null,
    releasedAt: formatDay(release?.releasedAt),
    releasedBy: release?.releasedBy || null,
    releaseNote: release?.releaseNote || null,

    // Drift: the decision under this release has changed since preparation.
    drifted: release?.shortlist?.drifted === true,
    driftNote: release?.shortlist?.note || null,
    currentShortlistState: release?.shortlist?.current?.state || null,

    downstream: downstreamState,
    downstreamLabel: downstreamState ? downstreamStateDisplay(downstreamState).label : null,
    downstreamTone: downstreamState ? downstreamStateDisplay(downstreamState).tone : 'unknown',
    /**
     * A quote that arrived under the firm's OTHER record, joined by a
     * CONFIRMED identity link. It still counts as this provider responding —
     * the write side refuses to prepare a second release beside such a quote,
     * so the read side agrees. Worth mentioning, never an error.
     */
    siblingMatch: (release?.downstream?.bidderKeyMatches || []).some(
      (match) => match?.matchedBy === 'confirmed_sibling_record'
    ),
    /**
     * A FLOOR, not a total: the backend caps this array at 50 with no flag and
     * no count, while it computes `downstream.state` over the uncapped set.
     * Never render this as "N responses".
     */
    matchedQuoteFloor: (release?.downstream?.bidderKeyMatches || []).length,

    package: release?.scopeSnapshot?.package || null,
    sendTruth: presentSendTruth(release),
  };
};

/** Awaiting a human first, then released, then withdrawn. */
/**
 * The backend returns releases ordered by a UUID primary key, which is no
 * order at all. What needs a human sorts first, then the oldest preparation,
 * then the name — so the list is stable across reads.
 */
export const sortReleases = (rows = []) =>
  [...rows].sort(
    (a, b) =>
      a.order - b.order ||
      String(a.preparedAt || '').localeCompare(String(b.preparedAt || '')) ||
      String(a.title || a.bidderKey || '').localeCompare(String(b.title || b.bidderKey || ''))
  );

/**
 * The package-level picture. Counts come from the backend's own facets, which
 * are computed over the releases it returned — so when the read was capped,
 * they are floors and this says so.
 */
const RESPONDED_DOWNSTREAM = ['RESPONDED', 'QUOTED', 'EVALUATED', 'AWARDED'];

export const summarizeReleases = (payload) => {
  const stateFacets = payload?.stateFacets || {};
  const downstreamFacets = payload?.downstreamFacets || {};

  /**
   * `downstreamFacets` is tallied over EVERY release the backend returned —
   * PREPARED and REVOKED rows included — because `compose_rfq_releases`
   * derives a downstream state for each row in one loop with no state filter.
   * A provider nobody has authorized yet therefore lands in NO_RESPONSE, and
   * paraphrasing that tally as "released and silent" would tell a PM an RFQ
   * went out that never did.
   *
   * So the response counts are partitioned over the rows themselves, where
   * each row carries its own `state` beside its own derived `downstream`. The
   * rows are the same set the facets were computed over, so the two agree on
   * everything except the partition.
   */
  const rows = Array.isArray(payload?.releases) ? payload.releases : [];
  const releasedRows = rows.filter((row) => row?.state === 'RELEASED');
  const responded = releasedRows.filter((row) =>
    RESPONDED_DOWNSTREAM.includes(row?.downstream?.state)
  ).length;
  const awaitingResponse = releasedRows.filter(
    (row) => row?.downstream?.state === 'NO_RESPONSE'
  ).length;

  return {
    count: payload?.count || 0,
    prepared: stateFacets.PREPARED || 0,
    released: stateFacets.RELEASED || 0,
    revoked: stateFacets.REVOKED || 0,
    /** Both of these describe RELEASED rows only. */
    awaitingResponse,
    responded,
    /**
     * The unpartitioned tallies, kept under names that cannot be mistaken for
     * a statement about who was released. Nothing renders these as responses.
     */
    downstreamAcrossAllStates: downstreamFacets,
    truncated: Boolean(payload?.truncated),
    countsAreFloors: Boolean(payload?.truncated),
    notes: payload?.notes || [],
  };
};

/**
 * Turn an RFQ refusal into something a PM can act on.
 *
 * Two of these are structured answers rather than failures, and both are
 * blocking by design — there is deliberately no client-side bypass for either,
 * because both exist to protect the integrity of the quote lineage.
 */
export const presentRfqError = (classified) => {
  const code = classified?.code || null;
  const detail = classified?.detail || null;

  if (code === 'identity_review_required') {
    return {
      kind: 'identity-review',
      title: 'Identity review required',
      /**
       * Every candidate is reported and NONE is chosen — the same doctrine the
       * Provider Passport applies to conflicting facts. React must not pick
       * one, fuzzy-match, or prefer a source.
       */
      explanation:
        'OMNΛS cannot safely connect this provider to the commercial RFQ record yet. More than one confirmed record could be the firm, and choosing between them is a human decision — picking one automatically could start a quote under the wrong company.',
      backendMessage: classified.message,
      candidates: detail?.candidates || [],
      candidateCount: detail?.candidateCount ?? (detail?.candidates || []).length,
      candidatesTruncated: detail?.candidatesTruncated === true,
      resolvable: true,
    };
  }


  if (code === 'duplicate_provider_lineage') {
    return {
      kind: 'duplicate-lineage',
      title: 'This firm is already on this package',
      explanation:
        'The same organization is already prepared or already quoting on this package through a different record. OMNΛS refused the second commercial path rather than let one firm enter the package twice, which would corrupt the quote comparison.',
      backendMessage: classified.message,
      existingBidderKey: detail?.existingBidderKey || null,
      resolvedBidderKey: detail?.resolvedBidderKey || null,
      resolvable: true,
    };
  }

  if (code === 'shortlist_entry_not_eligible') {
    return {
      kind: 'not-eligible',
      title: 'A human decision is needed first',
      explanation:
        'Only a provider a person has shortlisted or marked preferred can be prepared. Automation proposing a candidate is not a shortlist decision, and a provider who was excluded stays excluded until someone shortlists them again.',
      backendMessage: classified.message,
      resolvable: true,
    };
  }

  if (code === 'rfq_release_conflict') {
    return {
      kind: 'raced',
      title: 'Someone prepared this at the same moment',
      explanation:
        'Two preparations raced and the database kept one. Nothing was lost — reload to see the release that won.',
      backendMessage: classified.message,
      resolvable: true,
    };
  }

  if (code === 'package_not_on_project' || code === 'invalid_state_transition') {
    return {
      kind: 'stale-view',
      title: 'This view is out of date',
      explanation: 'Reload to see the current state of this package.',
      backendMessage: classified.message,
      resolvable: true,
    };
  }

  return null;
};

// ---------------------------------------------------------------------------
// Write outcomes — what a refusal should do to the screen
// ---------------------------------------------------------------------------

/**
 * The rule governing every write on this surface: what happens to the dialog,
 * the reason the human typed, and the reads that are now behind the record.
 *
 * The load-bearing half is `keepNote`. A person types a justification, the
 * write is refused, and discarding their words because the server said no
 * would be the rudest possible response — they would have to retype it to
 * find out whether the refusal was permanent. A refusal NEVER closes the
 * dialog and NEVER discards the note; only a write that actually stuck does.
 */
export const resolveWriteOutcome = (classified) => {
  if (!classified) {
    return { closeDialog: true, keepNote: false, reload: [], blocking: false };
  }
  const code = classified.code || null;
  const kind = classified.kind || null;

  // These two are structured answers, not failures. They get a dialog of
  // their own, so the write dialog closes — but the note is preserved,
  // because resolving the identity and retrying is the expected next act.
  if (code === 'identity_review_required' || code === 'duplicate_provider_lineage') {
    return { closeDialog: true, keepNote: true, reload: [], blocking: true };
  }

  // The view is behind the record. Re-read what moved, keep everything typed.
  if (code === 'shortlist_entry_not_eligible' || code === 'package_not_on_project') {
    return { closeDialog: false, keepNote: true, reload: ['releases', 'shortlist'], blocking: false };
  }
  if (code === 'rfq_release_conflict' || code === 'invalid_state_transition') {
    return { closeDialog: false, keepNote: true, reload: ['releases'], blocking: false };
  }
  if (kind === 'not-found') {
    return { closeDialog: false, keepNote: true, reload: ['releases', 'shortlist'], blocking: false };
  }

  // Everything else — refused, unreachable, invalid, unconfirmed, off. The
  // person may fix it and press again, so nothing they typed is thrown away.
  return { closeDialog: false, keepNote: true, reload: [], blocking: false };
};

/**
 * A payload is only this subject's answer when it was tagged for this subject.
 *
 * Every async read on the sourcing screen stores `{subjectId, data}` because
 * the headings, buttons and panels beside it are all derived from the CURRENT
 * selection. An untagged payload would render one package's providers under
 * another package's name — the worst failure this screen can produce.
 */
export const payloadForSubject = (tagged, subjectId, key = 'packageId') =>
  tagged && subjectId && tagged[key] === subjectId ? tagged.data : null;

/**
 * What a preparation actually did.
 *
 * `created: false` is the documented idempotent answer to a retry — a dropped
 * connection, a double click — and it must never read as a duplicate or as an
 * error. On that path the backend returns the identity AS PREPARED rather
 * than re-resolving it, so the answer describes the release that exists.
 */
export const presentPrepareResult = (result) => {
  const replayed = result?.created === false;
  const missing = result?.missingInformation || [];
  return {
    replayed,
    releaseId: result?.release?.id || null,
    bidderKey: result?.release?.bidderKey || result?.identity?.bidderKey || null,
    // True when the identity came from the release's stored snapshot.
    identityAsPrepared: result?.identity?.asPrepared === true,
    headline: replayed
      ? 'Already prepared — showing the existing handoff record.'
      : 'Prepared for RFQ. Nothing has been sent; a release still needs authorizing.',
    // Named gaps, in the backend's own words. Never summarised away.
    missingInformation: missing,
    sendTruth: presentSendTruth(result),
  };
};

/**
 * What an authorization actually did.
 *
 * `changed: false` means the release was already RELEASED. That is a replay,
 * not a second authorization and not a failure — reporting it as either would
 * make a human think they had done something twice.
 */
export const presentAuthorizeResult = (result) => {
  const replayed = result?.changed === false;
  return {
    replayed,
    releaseId: result?.release?.id || null,
    state: result?.release?.state || null,
    headline: replayed
      ? 'Already authorized — no second authorization was recorded.'
      : 'Released for RFQ. Internal authorization recorded; nothing was sent externally.',
    sendTruth: presentSendTruth(result),
  };
};

// ---------------------------------------------------------------------------
// One package, one story
// ---------------------------------------------------------------------------

/**
 * The eleven things a PM needs to know about one package, composed from
 * answers the backend already gave.
 *
 * This is a READING of state that exists, not a state machine. Every facet
 * names where its answer came from and links to the workspace that owns it;
 * nothing here decides eligibility, readiness, commercial standing or
 * execution impact, and a facet with no backend answer says so rather than
 * defaulting to a reassuring one.
 *
 * The distinction that matters: OMNΛS already knows all of this, but it knows
 * it in six different places. The story is the join, not new knowledge.
 */
export const STORY_TONE = ['good', 'active', 'attention', 'unknown'];

/**
 * The register keeps retired obligations as history. These are the backend's
 * own `INACTIVE_STATUSES` — a requirement in either state is no longer owed.
 */
const INACTIVE_REQUIREMENT_STATUSES = ['SUPERSEDED', 'CANCELLED'];

/**
 * A count taken over a truncated read is a floor. Every summarizer in this
 * file already works that out; the story is where it was being dropped.
 */
const floorNote = (detail, areFloors) => (areFloors ? `${detail} · counted so far` : detail);

/**
 * The realism verdict, said the way the realism engine's own display says it
 * — never as a raw enum token, and never in one flat tone that colours
 * INFEASIBLE the same blue as REALISTIC.
 *
 * This is the same mapping `readinessPresentation` renders on the panel
 * beside it. It is duplicated rather than imported because that module pulls
 * in icon components, and this one stays free of every dependency so it can
 * be reasoned about — and tested — as plain data.
 */
const REALISM_STORY = {
  REALISTIC: { label: 'Realistic', tone: 'good' },
  CONDITIONALLY_REALISTIC: { label: 'Conditionally realistic', tone: 'attention' },
  AT_RISK: { label: 'At risk', tone: 'attention' },
  INFEASIBLE: { label: 'Infeasible', tone: 'attention' },
  UNKNOWN: { label: 'Not established', tone: 'unknown' },
};

const realismStoryLabel = (classification) =>
  REALISM_STORY[classification]?.label || REALISM_STORY.UNKNOWN.label;

/**
 * An obligation of THIS package sitting on the critical path is worth a
 * PM's attention whatever the project-wide verdict, so it can only raise the
 * tone — it never softens a verdict the engine gave.
 */
const realismStoryTone = (classification, onCriticalPathHere) => {
  const base = REALISM_STORY[classification]?.tone || REALISM_STORY.UNKNOWN.tone;
  if (onCriticalPathHere > 0 && base !== 'attention') return 'attention';
  return base;
};

const storyFacet = (label, tone, value, detail, link) => ({
  label,
  tone,
  value,
  detail: detail || null,
  link: link || null,
});

export const presentPackageStory = ({
  row,
  sourcing,
  shortlist,
  releases,
  requirements,
  requirementsTruncated = false,
  awardProposals,
  realism,
} = {}) => {
  const facets = [];

  // 1. NEED — what this package is for.
  const profile = sourcing?.demandProfile || null;
  const trade = readField(profile?.trade);
  facets.push(
    storyFacet(
      'Need',
      trade.state === 'KNOWN' ? 'good' : 'unknown',
      trade.state === 'KNOWN' ? String(trade.value) : null,
      trade.state === 'KNOWN'
        ? provenanceLabel(trade.provenance)?.label || null
        : 'No trade is established for this package.',
      'requirements'
    )
  );

  // 2. REQUIREMENTS — the register rows tied to this package.
  if (!requirements) {
    facets.push(
      storyFacet('Requirements', 'unknown', null, 'Not read for this package.', 'requirements')
    );
  } else {
    /**
     * SUPERSEDED and CANCELLED rows stay in the register as history — the
     * backend's own `INACTIVE_STATUSES` — and counting them as live
     * obligations would overstate what this package still owes, and would
     * call a withdrawn requirement "mandatory".
     */
    const live = requirements.filter((r) => !INACTIVE_REQUIREMENT_STATUSES.includes(r?.status));
    const retired = requirements.length - live.length;
    const hardGates = live.filter((r) => r.isHardGate).length;
    facets.push(
      storyFacet(
        'Requirements',
        live.length === 0 ? 'unknown' : hardGates > 0 ? 'attention' : 'active',
        String(live.length),
        live.length === 0
          ? 'No live register requirement names this package.'
          : floorNote(
              `${hardGates} mandatory${retired > 0 ? ` · ${retired} retired` : ''}`,
              requirementsTruncated
            ),
        'requirements'
      )
    );
  }

  // 3. PROVIDER COVERAGE — how many candidates the catalog offered.
  const counts = sourcing?.counts || null;
  facets.push(
    storyFacet(
      'Provider coverage',
      !counts ? 'unknown' : (sourcing?.candidateCount || 0) > 0 ? 'active' : 'attention',
      counts ? String(sourcing?.candidateCount ?? 0) : null,
      counts ? 'candidates compared' : 'Matching has not been read for this package.',
      null
    )
  );

  // 4/5/6. The three judgements, kept apart exactly as they arrive.
  facets.push(
    storyFacet(
      'Eligible',
      !counts ? 'unknown' : (counts.pass || 0) > 0 ? 'good' : 'attention',
      counts ? String(counts.pass || 0) : null,
      counts ? `${counts.unknown || 0} unverified · ${counts.fail || 0} ineligible` : null,
      null
    )
  );

  /**
   * "No candidate carries a fit score" is a claim about the match set. When
   * matching was never read there IS no match set, and saying it anyway would
   * report an absence as a finding.
   */
  const scored = (sourcing?.candidates || []).filter(
    (c) => typeof c?.operationalFit?.score === 'number'
  );
  facets.push(
    storyFacet(
      'Operational fit',
      !counts ? 'unknown' : scored.length > 0 ? 'active' : 'unknown',
      !counts ? null : scored.length > 0 ? `${scored.length} scored` : '0',
      !counts
        ? 'Matching has not been read for this package.'
        : scored.length > 0
          ? 'A failed gate is never scored.'
          : 'No candidate carries a fit score.',
      null
    )
  );

  const known = (sourcing?.candidates || []).filter(
    (c) => c?.relationshipContext?.knownProvider === true
  ).length;
  facets.push(
    storyFacet(
      'Relationship',
      known > 0 ? 'active' : 'unknown',
      counts ? `${known} known` : null,
      'History, never a qualification.',
      null
    )
  );

  // 7. SHORTLIST — the human decisions.
  const shortlistSummary = shortlist ? summarizeShortlist(shortlist) : null;
  facets.push(
    storyFacet(
      'Shortlist',
      !shortlistSummary
        ? 'unknown'
        : shortlistSummary.awaiting > 0
          ? 'attention'
          : shortlistSummary.active > 0
            ? 'good'
            : 'unknown',
      shortlistSummary ? String(shortlistSummary.active) : null,
      !shortlistSummary
        ? 'Not read for this package.'
        : floorNote(
            shortlistSummary.awaiting > 0
              ? `${shortlistSummary.awaiting} awaiting a decision`
              : 'shortlisted or preferred',
            shortlistSummary.countsAreFloors
          ),
      null
    )
  );

  // 8. RFQ STATUS — internal records only. Never a send.
  const releaseSummary = releases ? summarizeReleases(releases) : null;
  facets.push(
    storyFacet(
      'RFQ handoff',
      !releaseSummary
        ? 'unknown'
        : releaseSummary.prepared > 0
          ? 'attention'
          : releaseSummary.released > 0
            ? 'good'
            : 'unknown',
      releaseSummary ? String(releaseSummary.released) : null,
      !releaseSummary
        ? 'Not read for this package.'
        : floorNote(
            releaseSummary.prepared > 0
              ? `${releaseSummary.prepared} awaiting release authorization`
              : 'released for RFQ — nothing transmitted externally',
            releaseSummary.countsAreFloors
          ),
      null
    )
  );

  // 9. QUOTE STATUS — per provider, from the backend's own verdict.
  facets.push(
    storyFacet(
      'Responses',
      !releaseSummary
        ? 'unknown'
        : releaseSummary.responded > 0
          ? 'active'
          : releaseSummary.awaitingResponse > 0
            ? 'attention'
            : 'unknown',
      releaseSummary ? String(releaseSummary.responded) : null,
      !releaseSummary
        ? 'Not read for this package.'
        : releaseSummary.released === 0
          ? 'Nobody has been released to quote yet.'
          : floorNote(
              `${releaseSummary.awaitingResponse} released with nothing back yet`,
              releaseSummary.countsAreFloors
            ),
      'package'
    )
  );

  // 10. AWARD / EXECUTION — the proposal record and the package's own status.
  const proposals = awardProposals || null;
  const awaitingAward = (proposals || []).filter((p) => p?.state === 'PROPOSED').length;
  /**
   * A bare proposal count is the wrong number to show large: the list holds
   * every recommendation ever calculated, rejected and superseded ones
   * included, so "4" would read as four live recommendations when none may be
   * waiting. What a PM has to act on is the PROPOSED count; the rest is
   * history and says so.
   */
  const awardValue = () => {
    if (row?.state === 'ASSIGNED') return 'Awarded';
    if (!proposals) return null;
    if (awaitingAward > 0) return String(awaitingAward);
    return proposals.length > 0 ? 'None waiting' : 'None';
  };
  facets.push(
    storyFacet(
      'Award',
      row?.state === 'ASSIGNED'
        ? 'good'
        : !proposals
          ? 'unknown'
          : awaitingAward > 0
            ? 'attention'
            : 'unknown',
      awardValue(),
      row?.state === 'ASSIGNED'
        ? 'Provider linked into procurement truth.'
        : !proposals
          ? 'Award workflow not read for this package.'
          : awaitingAward > 0
            ? `${awaitingAward} recommendation${
                awaitingAward === 1 ? '' : 's'
              } awaiting a human decision`
            : proposals.length > 0
              ? `${proposals.length} recommendation${
                  proposals.length === 1 ? '' : 's'
                } on record, all already decided`
              : 'No award recommendation has been calculated.',
      'package'
    )
  );

  /**
   * 11. EXECUTION IMPACT — the project's realism verdict, plus this package's
   * share of it.
   *
   * The join is `requirementId`, which both the register and the realism
   * engine mint from the same requirement row, so it is a join of two backend
   * answers rather than a recomputation of either.
   *
   * Two distinctions the backend draws and this must not blur:
   *   `criticalRequirements` is every HARD-GATE or SCHEDULE-DRIVING
   *   obligation the engine tracks — NOT the ones on the critical path.
   *   Whether an entry is actually on the critical path is its own per-entry
   *   `onCriticalPath` flag. Reporting the first as the second would tell a
   *   PM their package is driving the finish date when the engine never said
   *   so.
   */
  const tracked = (realism?.criticalRequirements || []).filter((r) => r?.requirementId);
  const trackedById = new Map(tracked.map((r) => [r.requirementId, r]));
  const trackedHere = (requirements || []).filter((r) => trackedById.has(r.requirementId));
  const onCriticalPathHere = trackedHere.filter(
    (r) => trackedById.get(r.requirementId)?.onCriticalPath === true
  ).length;

  facets.push(
    storyFacet(
      'Execution impact',
      !realism ? 'unknown' : realismStoryTone(realism.classification, onCriticalPathHere),
      realism?.classification ? realismStoryLabel(realism.classification) : null,
      !realism
        ? 'Project realism not read.'
        : !requirements
          ? "Requirements not read, so this package's share is unknown"
          : onCriticalPathHere > 0
            ? `${onCriticalPathHere} of this package's obligations sit on the critical path`
            : trackedHere.length > 0
              ? `${trackedHere.length} tracked obligation${
                  trackedHere.length === 1 ? '' : 's'
                }, none on the critical path`
              : 'No obligation of this package is driving the schedule',
      'readiness'
    )
  );

  return facets;
};

/* -------------------------------------------------------------------------
 * THE SHORTLIST READ MODEL, VALIDATED BEFORE IT IS CONSUMED
 * ---------------------------------------------------------------------- */

/** compose_shortlist() in backend/services/exchange_shortlist.py:686. */
export const SHORTLIST_CONTRACT_VERSION = 'exchange-shortlist-1';

/**
 * Decide what a shortlist payload actually is before anything reads it.
 *
 * This exists because of a measured crash. The consumer was
 * `(shortlist?.entries || []).forEach(...)`, and on an ARRAY payload
 * `.entries` resolves to `Array.prototype.entries` -- a FUNCTION, therefore
 * truthy, so the `|| []` fallback never fires and `.forEach` throws. The whole
 * Bid Package Workspace white-screened with "This screen failed to render":
 * the governed award surface, killed by a shape.
 *
 * Optional chaining is not validation. A shape has to be checked, not
 * defended against one field at a time.
 *
 * MALFORMED IS NOT EMPTY. A payload we cannot read must never render as
 * "0 bidders" or "no quotes" -- that is a commercial claim, and we would be
 * inventing it. It returns `malformed`, and the caller says so out loud.
 *
 * @param {unknown} payload whatever the shortlist endpoint returned
 * @returns {{status:'ok'|'absent'|'malformed', entries:Array, count:number,
 *            stateFacets:object, reason:string|null}}
 */
export const readShortlistContract = (payload) => {
  const bad = (reason) => ({ status: 'malformed', entries: [], count: 0, stateFacets: {}, reason });

  // Not asked for / not yet loaded. Distinct from unreadable.
  if (payload === null || payload === undefined) {
    return { status: 'absent', entries: [], count: 0, stateFacets: {}, reason: null };
  }
  // An Array is the exact shape that caused the crash: it is an object, it is
  // truthy, and it answers `.entries` with a function.
  if (Array.isArray(payload)) return bad('shortlist payload was an array, not the read model');
  if (typeof payload !== 'object') return bad(`shortlist payload was a ${typeof payload}`);
  if (!Array.isArray(payload.entries)) {
    return bad(
      payload.entries === undefined
        ? 'shortlist payload carried no entries'
        : `shortlist entries was a ${typeof payload.entries}, not an array`,
    );
  }
  // Version-pinned: a contract we do not recognise is not one we can read.
  if (payload.contractVersion !== undefined && payload.contractVersion !== SHORTLIST_CONTRACT_VERSION) {
    return bad(`shortlist contract ${String(payload.contractVersion)} is not ${SHORTLIST_CONTRACT_VERSION}`);
  }
  return {
    status: 'ok',
    entries: payload.entries,
    // `count` is the server's own figure; fall back to the array length only
    // when the server did not state one, never to a guess.
    count: typeof payload.count === 'number' ? payload.count : payload.entries.length,
    stateFacets:
      payload.stateFacets && typeof payload.stateFacets === 'object' && !Array.isArray(payload.stateFacets)
        ? payload.stateFacets
        : {},
    reason: null,
  };
};
